import { useState } from 'react';
import {
  CORE_CONSENT_SCOPES220,
  CORE_SERVICE_IDS220,
  activityCounts220,
  canShareAcrossServices220,
  coreActivityLabels220,
  coreConsentLabels220,
  coreRewardLabels220,
  coreServiceLabels220,
  growthEvents220,
  rewardTotalsForService220,
  type CoreConsentScope220,
  type CoreLearningEvent220,
  type CoreLedgerEntry220,
  type CoreServiceLink220,
} from '../domain/coreBridge220';
import { AppFrame, Card, Metric, PrimaryButton, SecondaryButton, StatusBanner } from '../components/ui';
import { GuideCharacter } from '../reactions/GuideCharacter';

const demoNexpaUserId = 'nexpa_2f8c41a90b7d3e6512aa';

const demoLinks: CoreServiceLink220[] = [
  { serviceId: 'mrm', linked: true, adapterConnected: true },
  { serviceId: 'lidia', linked: false, adapterConnected: false },
  { serviceId: 'bookquest', linked: false, adapterConnected: false },
  { serviceId: 'nexmap', linked: false, adapterConnected: false },
];

const demoEvents: CoreLearningEvent220[] = [
  { eventId: 'evt_demo_01', serviceId: 'mrm', activityType: 'article.read', contentId: '2026-08-01', result: 'completed', occurredAt: '2026-08-01T08:12:00+09:00', demoMode: true },
  { eventId: 'evt_demo_02', serviceId: 'mrm', activityType: 'quiz.complete', contentId: 'article_001', result: 'correct', occurredAt: '2026-08-01T08:15:00+09:00', demoMode: true },
  { eventId: 'evt_demo_03', serviceId: 'mrm', activityType: 'mission.complete', contentId: '2026-08-01', result: 'completed', occurredAt: '2026-08-01T18:40:00+09:00', demoMode: true },
  { eventId: 'evt_demo_04', serviceId: 'mrm', activityType: 'family.talk', contentId: 'msg_demo_01', result: 'completed', occurredAt: '2026-08-01T20:05:00+09:00', demoMode: true },
  { eventId: 'evt_demo_05', serviceId: 'mrm', activityType: 'puzzle.piece', contentId: 'puzzle_bank_001:piece_04', result: 'completed', occurredAt: '2026-08-01T20:06:00+09:00', demoMode: true },
  { eventId: 'evt_demo_06', serviceId: 'mrm', activityType: 'igloo.stage', contentId: 'igloo:9', result: 'completed', occurredAt: '2026-08-01T20:07:00+09:00', demoMode: true },
];

const demoLedger: CoreLedgerEntry220[] = [
  { entryId: 'ledger_demo_01', serviceId: 'mrm', reason: 'today.reward', rewardCode: 'points', quantity: 23, idempotencyKey: 'mrm:today:child_demo:2026-08-01:points', demoMode: true },
  { entryId: 'ledger_demo_02', serviceId: 'mrm', reason: 'today.reward', rewardCode: 'ice_brick', quantity: 1, idempotencyKey: 'mrm:today:child_demo:2026-08-01:ice_brick', demoMode: true },
];

const demoRealLedger: CoreLedgerEntry220[] = demoLedger.map((entry) => ({ ...entry, demoMode: false }));

type DemoStage = 'identity' | 'consent' | 'events' | 'ledger';
const demoStages: DemoStage[] = ['identity', 'consent', 'events', 'ledger'];
const demoStageLabels: Record<DemoStage, string> = {
  identity: '공통 식별자',
  consent: '동의 분리',
  events: '학습 이벤트',
  ledger: '보상 원장',
};

export function CoreBridgeDemoScreen220() {
  const [stageIndex, setStageIndex] = useState(0);
  const [consents, setConsents] = useState<Partial<Record<CoreConsentScope220, boolean>>>({ 'service.use': true, 'guardian.supervision': true });
  const stage = demoStages[stageIndex];

  const sharing = canShareAcrossServices220(consents);
  const counts = activityCounts220(demoEvents);
  const demoCounts = activityCounts220(demoEvents.map((event) => ({ ...event, demoMode: false })));
  const totals = rewardTotalsForService220(demoRealLedger, 'mrm');

  const toggle = (scope: CoreConsentScope220) => setConsents((current) => ({ ...current, [scope]: !current[scope] }));

  return <AppFrame title="MRM 2.20 NEXPA 코어 브리지 데모" backTo="/demo" screenClass="core-bridge-screen-2-20">
    <div className="demo-sample-banner">체험용 고정 연결 데이터 · 실제 계정과 완전 분리</div>
    <StatusBanner />

    <section className="core-bridge-hero-2-20">
      <GuideCharacter guide="snows" pose="research" state="guide" alt="코어 연결을 안내하는 스노스" priority/>
      <div>
        <p className="eyebrow">NEXPA Core Bridge</p>
        <h1>{demoStageLabels[stage]}</h1>
        <p>MRM의 활동이 NEXPA 공통 계약으로 기록되는 과정을 확인합니다. 화면·세계관·보상 규칙은 MRM이 그대로 소유합니다.</p>
      </div>
    </section>

    <div className="core-bridge-demo-steps-2-20">
      {demoStages.map((item, index) => (
        <button type="button" key={item} className={index === stageIndex ? 'active' : ''} onClick={() => setStageIndex(index)}>
          {index + 1}<span>{demoStageLabels[item]}</span>
        </button>
      ))}
    </div>

    <div className="core-bridge-demo-grid-2-20">
      {stage === 'identity' && <Card>
        <div className="card-heading"><div><span>공통 사용자 식별자</span><h2>기존 ID는 그대로 둡니다</h2></div></div>
        <p className="core-bridge-id-2-20">{demoNexpaUserId}</p>
        <div className="core-bridge-service-list-2-20">
          {CORE_SERVICE_IDS220.map((serviceId) => {
            const link = demoLinks.find((item) => item.serviceId === serviceId)!;
            return <div key={serviceId} className={link.linked ? 'linked' : ''}>
              <b>{coreServiceLabels220[serviceId]}</b>
              <span>{link.linked ? '연결됨' : '미연결'}</span>
              <small>{link.adapterConnected ? '어댑터 구현 완료' : '어댑터 계약만 준비'}</small>
            </div>;
          })}
        </div>
        <p className="core-bridge-note-2-20">MRM 사용자 ID는 변경되지 않고 매핑만 추가됩니다.</p>
      </Card>}

      {stage === 'consent' && <Card>
        <div className="card-heading"><div><span>동의 분리</span><h2>이용 동의가 연계 동의를 대신하지 않습니다</h2></div></div>
        <div className="core-bridge-consent-list-2-20">
          {CORE_CONSENT_SCOPES220.map((scope) => (
            <label key={scope}>
              <input type="checkbox" checked={Boolean(consents[scope])} onChange={() => toggle(scope)}/>
              <span>{coreConsentLabels220[scope]}</span>
            </label>
          ))}
        </div>
        <p className={sharing ? 'core-bridge-share-on-2-20' : 'core-bridge-share-off-2-20'}>
          {sharing ? '서비스 간 학습기록 연계가 허용된 상태입니다.' : '서비스 간 학습기록 연계가 꺼져 있어 다른 서비스에 기록이 보이지 않습니다.'}
        </p>
        <p className="core-bridge-note-2-20">연계 동의는 언제든 철회할 수 있고, 철회하면 즉시 조회 대상에서 빠집니다.</p>
      </Card>}

      {stage === 'events' && <Card>
        <div className="card-heading"><div><span>공통 학습 이벤트</span><h2>여섯 가지 활동이 같은 규격으로 남습니다</h2></div></div>
        <div className="core-bridge-event-list-2-20">
          {demoEvents.map((event) => (
            <div key={event.eventId}>
              <b>{coreActivityLabels220[event.activityType] ?? event.activityType}</b>
              <span>{event.contentId}</span>
              <small>{event.result} · demo</small>
            </div>
          ))}
        </div>
        <div className="core-bridge-metric-row-2-20">
          <Metric label="데모 이벤트" value={`${demoEvents.length}건`}/>
          <Metric label="성장 집계 반영" value={`${growthEvents220(demoEvents).length}건`}/>
          <Metric label="활동 종류" value={`${Object.keys(demoCounts).length}종`}/>
        </div>
        <p className="core-bridge-note-2-20">
          데모 이벤트는 {Object.keys(counts).length}건만 성장 집계에 반영됩니다. 실제 성장 기록은 데모의 영향을 받지 않습니다.
        </p>
      </Card>}

      {stage === 'ledger' && <Card>
        <div className="card-heading"><div><span>보상 원장</span><h2>지급 이력만 남고 규칙은 MRM이 가집니다</h2></div></div>
        <div className="core-bridge-ledger-list-2-20">
          {demoLedger.map((entry) => (
            <div key={entry.entryId}>
              <b>{coreRewardLabels220[entry.rewardCode] ?? entry.rewardCode}</b>
              <span>+{entry.quantity}</span>
              <small>{entry.idempotencyKey}</small>
            </div>
          ))}
        </div>
        <div className="core-bridge-metric-row-2-20">
          <Metric label="MRM 포인트" value={`${totals.points ?? 0}P`}/>
          <Metric label="MRM 얼음 벽돌" value={`${totals.ice_brick ?? 0}개`}/>
        </div>
        <p className="core-bridge-note-2-20">
          같은 중복 방지 키로 다시 지급하면 원장에 추가되지 않습니다. 다른 서비스의 포인트와 MRM 벽돌은 환전하지 않으므로 합산 값도 제공하지 않습니다.
        </p>
      </Card>}
    </div>

    <div className="demo-213-actions">
      <SecondaryButton onClick={() => { setStageIndex(0); setConsents({ 'service.use': true, 'guardian.supervision': true }); }}>초기화</SecondaryButton>
      <PrimaryButton onClick={() => setStageIndex((stageIndex + 1) % demoStages.length)}>다음 단계</PrimaryButton>
    </div>
  </AppFrame>;
}
